import { useEffect, useState } from 'react';
import type React from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useKnownAvailableParams, useNavigate } from '@change-corgi/core-react/router';

import type { EndorsementFormSchema } from '#src/app/pages/starterDashboardEndorsements/shared/utils';
import { useEndorsements } from '#src/app/shared/components/endorsements';
import type { EndorsementFormData } from '#src/app/shared/components/endorsements';
import { ENDORSEMENTS_EDIT_HASH } from '#src/app/shared/constants';
import { ImageUploadProvider, useImageUploadContext } from '#src/app/shared/hooks/imageUpload';
import { useToast } from '#src/app/shared/toast';

import { EndorsementForm } from '../EndorsementForm';
import { useCroppedPreview } from '../hooks/useCroppedPreview';
import { useEditEndorsement } from '../hooks/useEditEndorsement';
import { useEndorsementErrorToast } from '../hooks/useEndorsementErrorToast';

import { contentContainer, formSection, pageContainer, previewSection } from './EditEndorsement.styles';

function prepareInitialData(endorsement?: {
	name: string;
	title?: string | null;
	url?: string | null;
	content?: string | null;
	photo?: { userMedium: { url: string } } | null;
}) {
	if (!endorsement) return undefined;

	return {
		name: endorsement.name,
		title: endorsement.title || '',
		url: endorsement.url || '',
		statement: endorsement.content || '',
		photoUrl: endorsement.photo?.userMedium.url,
	};
}

function createHandleSubmit(actions: {
	handleFieldChange: (field: keyof EndorsementFormData, value: string) => void;
	handlePhotoUpload: (url: string | undefined) => void;
	handleNext: () => void;
}) {
	return (formData: EndorsementFormSchema) => {
		// React Hook Form manages the form state, so we just need to update
		// the hook's internal state for submission
		actions.handleFieldChange('name', formData.name || '');
		actions.handleFieldChange('title', formData.title || '');
		actions.handleFieldChange('statement', formData.statement || '');
		actions.handleFieldChange('url', formData.url || '');
		if (formData.photoUrl) {
			actions.handlePhotoUpload(formData.photoUrl);
		}

		// Trigger next step
		actions.handleNext();
	};
}

// oxlint-disable-next-line max-statements
const EditEndorsementContent: React.FC<{ readonly petitionId: string }> = ({ petitionId }) => {
	const { slug, endorsementId } = useKnownAvailableParams<{ slug: string; endorsementId: string }>();
	const { translate } = useI18n();
	const navigate = useNavigate();
	const { showToast } = useToast();
	const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
	const { dispatch: imageUploadDispatch } = useImageUploadContext();

	const {
		data: { endorsements, isLoading: isLoadingEndorsements },
	} = useEndorsements({ petitionSlugOrId: slug });

	const endorsement = endorsements.find((e) => e.id === endorsementId);
	const initialData = prepareInitialData(endorsement);

	const { data, actions, uploader } = useEditEndorsement({
		endorsementId,
		petitionId,
		initialData,
		onSuccess: () => {
			navigate(`/p/${slug}/edit?endorsementUpdated=true${ENDORSEMENTS_EDIT_HASH}`);
		},
	});

	const { croppedPreviewUrl, handleCroppedPreviewChange } = useCroppedPreview(data.image);

	// Initialize image upload context with existing photo
	useEffect(() => {
		if (endorsement?.photo?.userMedium.url && !data.image) {
			imageUploadDispatch({
				type: 'SET_IMAGE',
				payload: { image: endorsement.photo.userMedium.url, uploader },
			});
		}
	}, [endorsement?.id]);

	const initialPhotoUrl = endorsement?.photo?.userMedium.url;
	const isNewImage = !!data.image && data.image !== initialPhotoUrl;

	const cropProps = isNewImage
		? {
				crop: data.imageCrop,
				uploader,
				onImageCrop: actions.onImageCrop,
				croppedPreviewUrl,
				onCroppedPreviewChange: handleCroppedPreviewChange,
			}
		: {};

	useEndorsementErrorToast(data.error, actions.clearError);

	const handleBack = () => {
		navigate(`/p/${slug}/edit${ENDORSEMENTS_EDIT_HASH}`);
	};

	const handleSubmit = createHandleSubmit(actions);

	const handleInvalidSubmit = () => {
		showToast({
			variant: 'error',
			message: translate('corgi.petition.starter_dashboard_v2.endorsements.add.form_validation_error'),
		});
	};

	const handleConfirmSubmit = async () => {
		if (!data.isTermsAgreed) {
			showToast({
				variant: 'error',
				message: translate('corgi.petition.starter_dashboard_v2.endorsements.add.terms_required_error'),
				bottomPosition: 'bottom-[14vh] lg:bottom-[33vh]',
			});
			return;
		}
		await actions.handleSubmit();
	};

	// Don't render form until endorsement data is loaded
	if (isLoadingEndorsements || !endorsement || !initialData) {
		return null;
	}

	return (
		<EndorsementForm
			key={endorsementId}
			data-qa="edit-endorsement-form"
			title={translate('corgi.petition.starter_dashboard_v2.endorsements.edit.page_title')}
			backButtonAriaLabel={translate('corgi.petition.starter_dashboard_v2.endorsements.edit.back_button_aria_label')}
			defaultValues={initialData}
			isSubmitting={data.isSubmitting}
			isHelpModalOpen={isHelpModalOpen}
			isSubmitModalOpen={data.isSubmitModalOpen}
			onBack={handleBack}
			onSubmit={handleSubmit}
			onInvalidSubmit={handleInvalidSubmit}
			onHelpClick={() => setIsHelpModalOpen(true)}
			onCloseHelpModal={() => setIsHelpModalOpen(false)}
			onCloseSubmitModal={() => actions.setIsSubmitModalOpen(false)}
			onConfirmSubmit={handleConfirmSubmit}
			pageContainer={pageContainer()}
			contentContainer={contentContainer()}
			formSection={formSection()}
			previewSection={previewSection()}
			testId="edit-endorsement-page"
			uppy={uploader.uppy}
			image={data.image ?? data.formData.photoUrl}
			uploadErrorMessage={data.imageUploadErrorMessage}
			isTermsAgreed={data.isTermsAgreed}
			onTermsChange={actions.handleTermsChange}
			{...cropProps}
		/>
	);
};

export const EditEndorsement: React.FC<EditEndorsementProps> = ({ petitionId }) => {
	return (
		<ImageUploadProvider>
			<EditEndorsementContent petitionId={petitionId} />
		</ImageUploadProvider>
	);
};

type EditEndorsementProps = {
	readonly petitionId: string;
};
